import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { IBaseProps } from '@rbi-ctg/frontend';
import {
  ICustomizableContent,
  IGetAppCustomizableContent,
  ILoyaltyCustomizableContent,
  IPromotionsCustomizableContent,
  IRollUpTheRim,
} from '@rbi-ctg/menu';
import { useSingleAppDataQuery } from 'generated/sanity-graphql';
import {
  getAppDownloadCustomizableContent,
  getCustomizableContent,
  getCustomizableImageContent,
  getLoyaltyCustomizableContent,
} from 'remote/api/customizable-content';
import getRollUpTheRimContent from 'remote/api/roll-up-the-rim';
import noop from 'utils/noop';

import { useLocale } from '../intl';
import { useNetworkContext } from '../network';

interface IAppDataState {
  appDataId: string;
  isAppDataLoading: boolean;
}

interface ICustomizableContentState {
  content: ICustomizableContent;
  isLoading: boolean;
}

interface IRollUpTheRimState {
  rollUpTheRim: IRollUpTheRim;
  isLoadingRollUpTheRim: boolean;
}

interface IGetAppCustomizableContentState {
  getAppContent: IGetAppCustomizableContent;
  isLoadingGetApp: boolean;
}

interface ILoyaltyCustomizableContentState {
  loyaltyContent: ILoyaltyCustomizableContent;
  isLoadingLoyalty: boolean;
}

interface IPromotionsCustomizableContentState {
  promotionsContent: IPromotionsCustomizableContent;
  isLoadingPromotions: boolean;
}

interface ICustomizableContentErrorState {
  hasBlockingCustomizableContentError: boolean;
}

interface IGetAppRequest {
  requestAppDownloadCustomizableContent: () => void;
}

interface IGetLoyaltyRequest {
  requestLoyaltyCustomizableContent: () => void;
}

interface IGetRollUpTheRimRequest {
  requestRollUpTheRimContent: () => void;
}

export type TCustomizableContentContext = ICustomizableContentErrorState &
  IAppDataState &
  ICustomizableContentState &
  IGetAppCustomizableContentState &
  ILoyaltyCustomizableContentState &
  IRollUpTheRimState &
  IGetAppRequest &
  IGetLoyaltyRequest &
  IGetRollUpTheRimRequest;

export const CustomizableContentContext = createContext<TCustomizableContentContext>({
  appDataId: '',
  isAppDataLoading: true,
  hasBlockingCustomizableContentError: false,
  content: {} as ICustomizableContent,
  isLoading: true,
  getAppContent: {} as IGetAppCustomizableContent,
  isLoadingGetApp: true,
  loyaltyContent: {} as ILoyaltyCustomizableContent,
  isLoadingLoyalty: true,
  rollUpTheRim: {} as IRollUpTheRim,
  isLoadingRollUpTheRim: true,
  requestLoyaltyCustomizableContent: noop,
  requestAppDownloadCustomizableContent: noop,
  requestRollUpTheRimContent: noop,
});

export const useCustomizableContent = () => useContext(CustomizableContentContext);

export function CustomizableContentProvider({ children }: IBaseProps) {
  const { query, defaultErrorHandler } = useNetworkContext();
  const { locale } = useLocale();

  const { data: allAppData, loading: isAppDataLoading } = useSingleAppDataQuery();

  const getAppData = () => {
    let appDataId = '';
    if (allAppData && allAppData.allAppData && allAppData.allAppData.length) {
      appDataId = allAppData.allAppData[0]._id;
    }
    return { appDataId, isAppDataLoading };
  };

  const [hasBlockingError, setHasBlockingError] = useState(false);

  const [customizableContent, setCustomizableContent] = useState<ICustomizableContentState>({
    content: {} as ICustomizableContent,
    isLoading: true,
  });
  const [rollUpTheRimContent, setRollUpTheRimContent] = useState<IRollUpTheRimState>({
    rollUpTheRim: {} as IRollUpTheRim,
    isLoadingRollUpTheRim: true,
  });

  const [getAppCustomizableContent, setGetAppCustomizableContent] = useState<
    IGetAppCustomizableContentState
  >({
    getAppContent: {} as IGetAppCustomizableContent,
    isLoadingGetApp: true,
  });

  const [loyaltyCustomizableContent, setLoyaltyCustomizableContent] = useState<
    ILoyaltyCustomizableContentState
  >({
    loyaltyContent: {} as ILoyaltyCustomizableContent,
    isLoadingLoyalty: true,
  });

  // Only retrieve the app data once or when locale changes
  useEffect(() => {
    Promise.all([query(getCustomizableContent()), query(getCustomizableImageContent())])
      .then(([staticContent, imageContent]) => {
        const content = Object.assign({}, staticContent, imageContent);

        setCustomizableContent({ content, isLoading: false });
      })
      .catch(error => {
        setHasBlockingError(true);
        defaultErrorHandler(error);
        setCustomizableContent(currentContent => ({ ...currentContent, isLoading: false }));
      });
  }, [locale]); // eslint-disable-line react-hooks/exhaustive-deps

  const requestLoyaltyCustomizableContent = useCallback(() => {
    query(getLoyaltyCustomizableContent())
      .then(loyaltyContent => {
        setLoyaltyCustomizableContent({
          loyaltyContent,
          isLoadingLoyalty: false,
        });
      })
      .catch(error => {
        defaultErrorHandler(error);
        setLoyaltyCustomizableContent(currentContent => ({
          ...currentContent,
          isLoadingLoyalty: false,
        }));
      });
  }, [locale]); // eslint-disable-line react-hooks/exhaustive-deps

  const requestAppDownloadCustomizableContent = useCallback(() => {
    query(getAppDownloadCustomizableContent())
      .then(getAppContent => {
        setGetAppCustomizableContent({ getAppContent, isLoadingGetApp: false });
      })
      .catch(error => {
        defaultErrorHandler(error);
        setGetAppCustomizableContent(currentContent => ({
          ...currentContent,
          isLoadingGetApp: false,
        }));
      });
  }, [locale]); // eslint-disable-line react-hooks/exhaustive-deps

  const requestRollUpTheRimContent = useCallback(() => {
    query(getRollUpTheRimContent())
      .then(rollUpTheRim => {
        setRollUpTheRimContent({ rollUpTheRim, isLoadingRollUpTheRim: false });
      })
      .catch(error => {
        defaultErrorHandler(error);
        setRollUpTheRimContent(currentData => ({ ...currentData, isLoadingRollUpTheRim: false }));
      });
  }, [locale]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <CustomizableContentContext.Provider
      value={{
        hasBlockingCustomizableContentError: hasBlockingError,
        ...getAppData(),
        ...customizableContent,
        ...getAppCustomizableContent,
        ...loyaltyCustomizableContent,
        ...rollUpTheRimContent,
        requestAppDownloadCustomizableContent,
        requestLoyaltyCustomizableContent,
        requestRollUpTheRimContent,
      }}
    >
      {children}
    </CustomizableContentContext.Provider>
  );
}

export default CustomizableContentContext.Consumer;
